import { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useContent } from '@/lib/content';
import { buildWaLink } from '@/config/brand';
import { trackEvent } from '@/lib/analytics';
import { Reveal } from '@/components/Reveal';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { WhatsAppFloat } from '@/components/WhatsAppFloat';

export default function Pricing() {
  const { packages, packageCategories, loaded, brand } = useContent();

  const groups = [];
  (packageCategories || []).forEach((c) => groups.push([c.label, []]));
  (packages || []).forEach((p) => {
    let g = groups.find(([label]) => label === p.category);
    if (!g) {
      g = [p.category || 'Other Packages', []];
      groups.push(g);
    }
    g[1].push(p);
  });
  const visibleGroups = groups.filter(([, items]) => items.length > 0);

  useEffect(() => {
    document.title = 'Packages & Pricing | Events by AJ — Event Planner in Hyderabad';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-ink text-cream min-h-screen">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />
      <main className="pt-28 sm:pt-36 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="caption-label mb-4">Transparent Starting Prices</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-cream">
              Packages & <span className="italic text-champagne">Pricing</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/60 font-light leading-relaxed">
              Every celebration is customised around you — these are honest starting points.
              Tell us about your event and we&rsquo;ll craft an exact quote.
            </p>
          </Reveal>

          {loaded && (
            <div className="mt-14 space-y-16" data-testid="pricing-groups">
              {visibleGroups.map(([label, items]) => (
                <div key={label} data-testid={`pricing-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <Reveal>
                    <div className="flex items-center gap-5 mb-8">
                      <h2 className="font-serif text-2xl sm:text-3xl font-light text-cream shrink-0">{label}</h2>
                      <span className="h-px flex-1 bg-gradient-to-r from-champagne/50 to-transparent" />
                    </div>
                  </Reveal>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="pricing-grid">
                    {items.map((p, i) => (
                      <Reveal key={p.id} delay={(i % 3) * 0.08} className="relative bg-ink p-8 sm:p-10 flex flex-col hover:bg-surface transition-colors duration-500">
                        {p.popular && (
                          <span data-testid={`package-popular-${p.id}`} className="absolute top-0 right-0 bg-champagne text-ink text-[10px] uppercase tracking-[0.25em] font-semibold px-4 py-2 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> Most Popular
                          </span>
                        )}
                        <h3 className="font-serif text-2xl sm:text-3xl font-light text-cream leading-tight">{p.name}</h3>
                        <p data-testid={`package-price-${p.id}`} className="mt-4 font-serif text-2xl sm:text-3xl text-champagne">{p.price}</p>
                        <p className="mt-4 text-white/60 font-light text-sm leading-relaxed flex-1">{p.description}</p>
                        <a
                          data-testid={`package-enquire-${p.id}`}
                          href={buildWaLink(brand.whatsapp, `Hi Events by AJ, I'm interested in your ${p.name} package (${p.price}). Please share details.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('package_enquire', { package: p.name })}
                          className="mt-8 btn-outline justify-center"
                        >
                          <FaWhatsapp size={15} /> Enquire About This
                        </a>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
              {visibleGroups.length === 0 && (
                <p className="text-center text-white/40 font-light">Packages are being updated — please check back soon.</p>
              )}
            </div>
          )}

          <Reveal className="mt-14 text-center">
            <p className="font-serif text-2xl sm:text-3xl font-light text-cream">Planning Something Special?</p>
            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
              <a data-testid="pricing-quote-btn" href="/#enquiry" className="btn-gold">Get Your Free Quote</a>
              <a data-testid="pricing-calculator-btn" href="/birthday-calculator" className="btn-outline">Try the Birthday Calculator</a>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
